import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import { VariantCollectionSalesCard } from "~/components/forecast/VariantCollectionSalesCard"
import { VariantComparisonCard } from "~/components/forecast/VariantComparisonCard"
import { VariantSizeSplitGraph } from "~/components/forecast/VariantSizeSplitGraph"
import VariantTableCard, {
  VariantsTableRow,
} from "~/components/product/VariantTableCard/VariantTableCard"
import { useVariantContext } from "~/context/VariantProvider"
import { useSkuStore } from "~/providers/sku-store-provider"
import { CustomerSalesCard } from "~/components/forecast/CustomerSalesCard"
import { usePageTracking } from "~/hooks/usePageTracking"
import VariantGraph from "~/components/product/VariantGraph"
import { AnalysisPage } from "~/components/design-system/pages"
import VariantInspector from "~/components/product/VariantInspector"

export const Route = createFileRoute("/_auth/product/$productId/_layout/demand")({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Product - Demand")

  const { variants, isMonthly, startDate, endDate } = useVariantContext()
  const variantIds = useMemo(() => variants.map((v) => v.variantId), [variants])

  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)

  const hasSizes = variants.some((v) => v.sizes.length > 0)

  return (
    <AnalysisPage
      inspector={
        <VariantInspector
          sections={["image", "details", "similarVariants", "adjustments", "comments"]}
        />
      }
    >
      <div className="min-h-full flex flex-col gap-4">
        <VariantGraph
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          graphHeight={400}
          netSalesMode="both"
        />

        <VariantTableCard
          variantIds={variantIds}
          monthlyView={isMonthly}
          startDate={startDate}
          endDate={endDate}
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          rows={[
            VariantsTableRow.Sales,
            VariantsTableRow.SalesIndex,
            VariantsTableRow.NetSales,
            VariantsTableRow.Forecast,
            VariantsTableRow.RawForecasts,
            VariantsTableRow.PreOrders,
            VariantsTableRow.Returns,
            VariantsTableRow.ForecastedReturns,
          ]}
        />
        <div className="flex flex-row flex-wrap gap-4">
          <VariantComparisonCard />

          {hasSizes && startDate && endDate && (
            <VariantSizeSplitGraph
              variantIds={variantIds}
              dateRange={{ start: startDate, end: endDate }}
            />
          )}
        </div>
        <div className="flex flex-row gap-4">
          <VariantCollectionSalesCard
            variantIds={variantIds}
            warehouseId={selectedWarehouseId}
            channel={selectedChannel}
            className="w-1/2"
          />
          <CustomerSalesCard
            variantIds={variantIds}
            warehouseId={selectedWarehouseId}
            channel={selectedChannel}
            className="w-1/2"
          />
        </div>
      </div>
    </AnalysisPage>
  )
}
